export interface Activity {
  id: string;
  timestamp: string;
  type: "alert" | "prediction" | "resource" | "user" | "system";
  title: string;
  description: string;
  districtName?: string;
  severity: "info" | "warning" | "critical";
}

const activityTemplates: Record<string, { titles: string[]; descriptions: string[] }> = {
  alert: {
    titles: [
      "High-Risk Alert Generated",
      "Medium-Risk Alert Triggered",
      "Alert Status Updated",
      "Cross-District Spread Warning",
    ],
    descriptions: [
      "AI model flagged {district} with 38% case growth and favorable breeding conditions.",
      "Rainfall accumulation crossed 500mm threshold. Surveillance enhanced.",
      "Alert moved from Active to Acknowledged by field coordinator.",
      "Neighboring district spike detected. Contingency stock pre-positioned.",
    ],
  },
  prediction: {
    titles: [
      "Weekly Model Retrained",
      "Prediction Updated",
      "Confidence Score Improved",
      "Forecast Horizon Extended",
    ],
    descriptions: [
      "XGBoost ensemble retrained on latest 7-day data. RMSE down 3.2%.",
      "3-month forecast for {district} revised based on new weather patterns.",
      "Model calibration improved confidence interval coverage to 94%.",
      "12-week horizon predictions now available for all 20 districts.",
    ],
  },
  resource: {
    titles: [
      "RDT Stock Deployed",
      "ACT Buffer Stock Moved",
      "LLIN Distribution Launched",
      "Emergency Vehicle Dispatched",
    ],
    descriptions: [
      "500 RDT kits dispatched to {district} health centers ahead of predicted surge.",
      "1,200 blister packs repositioned from low-risk district to {district} warehouse.",
      "Mass net distribution campaign started covering 12,000 households.",
      "Spray truck deployed to stagnant water zones in {district}.",
    ],
  },
  user: {
    titles: [
      "New Team Member Added",
      "Role Assignment Changed",
      "Alert Acknowledged",
      "Report Exported",
    ],
    descriptions: [
      "Dr. Kwame Mensah joined Malaria Consortium as Organization User.",
      "Paul Ekobe elevated from Viewer to Organization User.",
      "Dr. Amina Ngono acknowledged alert ALT-0003 for Garoua Urban.",
      "Weekly CSV report downloaded by Lucia Abang.",
    ],
  },
  system: {
    titles: [
      "API Key Rotated",
      "Weather Data Synced",
      "Backup Completed",
      "Security Patch Applied",
    ],
    descriptions: [
      "Production API key SK-live-7a3f regenerated for enhanced security.",
      "Satellite weather ingestion completed for all 20 districts.",
      "Full database snapshot archived to cold storage.",
      "Deno runtime updated to latest stable release.",
    ],
  },
};

const districtNames = [
  "Yaoundé Central", "Douala Metropolitan", "Bamenda Urban", "Maroua Central",
  "Bafoussam Central", "Garoua Urban", "Kumba Central", "Nkongsamba Urban",
  "Bertoua Central", "Edéa Metropolitan", "Kousséri Urban", "Ngaoundéré Central",
  "Limbe Metropolitan", "Buea Central", "Foumban Urban", "Ebolowa Central",
  "Dschang Urban", "Kribi Coastal", "Guider Urban", "Mbalmayo Central",
];

function generateActivities(): Activity[] {
  const data: Activity[] = [];
  const baseDate = new Date("2025-05-09T08:00:00");
  const types: ("alert" | "prediction" | "resource" | "user" | "system")[] = [
    "alert", "prediction", "resource", "user", "system",
  ];
  const severities: Record<Activity["type"], Activity["severity"][]> = {
    alert: ["warning", "critical", "warning", "info"],
    prediction: ["info", "info", "info", "warning"],
    resource: ["info", "info", "warning", "critical"],
    user: ["info", "info", "info", "info"],
    system: ["info", "info", "info", "info"],
  };

  for (let i = 0; i < 24; i++) {
    const type = types[i % types.length];
    const tpl = activityTemplates[type];
    const title = tpl.titles[i % tpl.titles.length];
    const desc = tpl.descriptions[i % tpl.descriptions.length];
    const district = districtNames[i % districtNames.length];

    const timestamp = new Date(baseDate);
    timestamp.setMinutes(baseDate.getMinutes() - i * 45);

    data.push({
      id: `ACT-${String(i + 1).padStart(4, "0")}`,
      timestamp: timestamp.toISOString(),
      type,
      title,
      description: desc.replace("{district}", district),
      districtName: desc.includes("{district}") ? district : undefined,
      severity: severities[type][i % severities[type].length],
    });
  }

  return data;
}

export const activities = generateActivities();